import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VbrickPlayerModule } from '@vbrick/vbrick-player';
import { PLAYER_TRANSLATIONS } from './PlayerTestPageTranslations';
import { StreamData } from '../../shared/stream.token';
import { VideogularComponent } from '@vbrick/vbrick-player';
import { StreamService } from '../../shared/stream.service';
import { RaceSessionService } from '../../timing-tower/race-session.service';
import { ScriptLoaderService } from '../../shared/script-loader.service';
import { FeatureSettingsService } from '../../shared/feature-settings.service';
import { toSignal } from '@angular/core/rxjs-interop';

let playerId = 0;

@Component({
  selector: 'vb-lxp-video-player',
  standalone: true,
  imports: [CommonModule, VbrickPlayerModule],
  templateUrl: './vb-lxp-video-player.component.html',
  styleUrl: './vb-lxp-video-player.component.css',
})
export class VbLxpVideoPlayerComponent implements OnInit {
  public readonly PLAYER_TRANSLATIONS = PLAYER_TRANSLATIONS;

  public vgAPI!: VideogularComponent;

  public featureService = inject(FeatureSettingsService);
  public streamService = inject(StreamService);
  public scriptLoaderService = inject(ScriptLoaderService);
  public raceSessionService = inject(RaceSessionService);
  public raceHappening = computed(() => this.raceSessionService.raceHappening());
  private readonly isMockDataType =
    (new URLSearchParams(window.location.search).get('ldatatype') || '').toLowerCase() === 'mock';

  public featureSettings = toSignal(this.featureService.featureSettings$);
  public raceStreamDisabled = computed(() => this.featureSettings()?.race.disableStream);
  public playerId: number;
  private readonly streamDataVersion = signal(0);
  private previousRaceHappening: boolean | null = null;
  private _streamData: StreamData | null = null;
  public get streamData(): StreamData | null {
    return this._streamData;
  }
  public set streamData(value: StreamData | null) {
    this._streamData = value;
    this.streamDataVersion.update((version) => version + 1);
  }

  public getStreamDataVersion() {
    return this.streamDataVersion();
  }
  public isMainPlayer = false;
  public isTwitchPlayer = false;
  public twitchPlayer: any;
  public twitchPlayerLoaded = false;
  public vgApi: VideogularComponent;
  private readonly raceHappeningEffect = effect(() => {
    const currentRaceHappening = this.raceHappening();

    if (this.previousRaceHappening === null) {
      this.previousRaceHappening = currentRaceHappening;
      return;
    }

    if (
      this.previousRaceHappening !== currentRaceHappening &&
      this.isMainPlayer &&
      this.streamData
    ) {
      // Force reconfiguration by temporarily clearing the current config
      this.streamPlayerConfig = null;
      setTimeout(() => {
        this.setStreamPlayerConfig(this.streamData);
      }, 100);
    }

    this.previousRaceHappening = currentRaceHappening;
  });

  public streamPlayerConfig = {
    sources: [
      {
        is360Enabled: false,
        isLive: true,
        label: `Main Feed`,
        qValue: 2800,
        src: '',
        type: 'HLS',
      },
    ],
    autoPlay: true,
    isLive: true,
    playerConfig: {
      playerControls: {
        settings: false,
        hideMuteButton: true,
      },
    },
  };

  public ngOnInit(): void {
    this.playerId = ++playerId;
    console.warn('featureSettings', this.featureSettings());
  }

  public setStreamData(streamData: StreamData) {
    this.streamData = streamData;
    this.isMainPlayer = streamData?.id?.toLowerCase().startsWith('main-stream') || false;
    this.isTwitchPlayer = streamData?.streamingPartner === 'Twitch';
    this.previousRaceHappening = this.raceHappening();
    this.setStreamPlayerConfig(this.streamData);
  }

  public setStreamPlayerConfig(streamData: StreamData) {
    const playbackData = this.resolveStreamSourceUrl(streamData);
    const currentUrl = this.streamPlayerConfig?.sources?.[0]?.src;

    // Only update if the URL is different from the current one
    if (currentUrl === playbackData.url) {
      return;
    }

    this.streamPlayerConfig = {
      sources: [
        {
          is360Enabled: false,
          isLive: playbackData.isLive,
          label: `${streamData?.teamName} - Car #${streamData?.carNumber}`,
          qValue: 2800,
          src: playbackData.url,
          type: 'HLS',
        },
      ],
      autoPlay: true,
      isLive: playbackData.isLive,
      playerConfig: {
        playerControls: {
          settings: false,
          hideMuteButton: true,
        },
      },
    };
  }

  public disablePlayback() {
    this.streamPlayerConfig = null;
    if (this.streamData) {
      this.streamData.hlsPlaybackUrl = '';
    }
  }

  public enablePlayback(url: string) {
    if (!this.streamData) {
      return;
    }

    this.streamData.hlsPlaybackUrl = url;
    this.setStreamPlayerConfig(this.streamData);
  }

  private resolveStreamSourceUrl(streamData: StreamData): { url: string; isLive: boolean } {
    let playbackData;
    if (!this.isMainPlayer) {
      playbackData = {
        url: streamData?.hlsPlaybackUrl,
        isLive: true,
      };
    } else if (
      streamData?.hlsPromotionalVideoUrl &&
      (this.isMockDataType || !this.raceHappening())
    ) {
      playbackData = {
        url: streamData?.hlsPromotionalVideoUrl,
        isLive: false,
      };
    } else {
      playbackData = {
        url: streamData?.hlsPlaybackUrl,
        isLive: true,
      };
    }
    return playbackData;
  }

  public onPlayerReady(vgAPI: VideogularComponent): void {
    this.vgAPI = vgAPI;
    setTimeout(() => {
      if (!this.isMainPlayer) {
        this.vgAPI.setVolume(0);
      }
    }, 100);
  }

  public onPlaybackEnded(): void {
    if (this.vgAPI?.activeSource?.src === this.streamData?.hlsPromotionalVideoUrl) {
      setTimeout(() => {
        this.vgAPI?.play();
      }, 100);
    }
  }

  public setTwitchStream() {
    this.loadTwitchPlayer();
  }

  private loadTwitchPlayer() {
    setTimeout(() => {
      this.scriptLoaderService.loadScript('https://player.twitch.tv/js/embed/v1.js').then(() => {
        this.playTwitchStream();
      });
    }, 200);
  }

  private playTwitchStream() {
    try {
      const options = {
        width: '100%',
        height: '100%',
        allowfullscreen: true,
        channel: 'sromotorsports',
        autoplay: true,
      };
      this.twitchPlayer = new (window as any).Twitch.Player('twitch-player-container', options);
      this.twitchPlayerLoaded = true;
    } catch (error) {
      console.error('Error loading Twitch player:', error);
    }
  }
}
